import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogRef,
  MatDialogActions,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';

export interface ChooseThreeDialogData {
  title: string;
  message: string;
  option1Text: string;
  option2Text: string;
  option3Text: string;
  option1Color?: 'primary' | 'accent' | 'warn';
  option2Color?: 'primary' | 'accent' | 'warn';
  option3Color?: 'primary' | 'accent' | 'warn';
  option1Value?: any;
  option2Value?: any;
  option3Value?: any;
  option1Disabled?: boolean;
  option2Disabled?: boolean;
  option3Disabled?: boolean;
}

export interface ChooseThreeDialogResult {
  choice: 'option1' | 'option2' | 'option3' | null;
  value?: any;
}

@Component({
  selector: 'app-choose-three-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule
  ],
  template: `
    <div class="dialog-container">
      <h5 mat-dialog-title class="dialog-title">{{ data.title }}</h5>
      <div mat-dialog-content class="dialog-content">
        <div [innerHTML]="data.message" class="message-text"></div>
      </div>
      <div mat-dialog-actions class="dialog-actions">
        <button 
          mat-flat-button 
          [class]="getButtonClass(data.option1Color || 'primary')"
          [disabled]="data.option1Disabled"
          (click)="onChoice('option1')"
          class="action-btn option1-btn"
          cdkFocusInitial>
          {{ data.option1Text }}
        </button>
        <button 
          mat-flat-button 
          [class]="getButtonClass(data.option2Color || 'primary')"
          [disabled]="data.option2Disabled"
          (click)="onChoice('option2')"
          class="action-btn option2-btn">
          {{ data.option2Text }}
        </button>
        <button 
          mat-flat-button 
          [class]="getButtonClass(data.option3Color || 'warn')"
          [disabled]="data.option3Disabled"
          (click)="onChoice('option3')"
          class="action-btn option3-btn">
          {{ data.option3Text }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 0;
      min-width: 480px;
    }

    .dialog-title {
      font-size: 18px;
      font-weight: 600;
      color: #2a3547;
      margin: 0;
      padding: 24px 24px 16px 24px;
      line-height: 1.4;
    }

    .dialog-content {
      padding: 0 24px 24px 24px !important;
      color: #5a6a85;
    }

    .message-text {
      font-size: 14px;
      line-height: 1.6;
      color: #5a6a85;
    }

    /* Style pour mettre ATTENTION en rouge et en gras */
    .message-text ::ng-deep strong,
    .message-text ::ng-deep b {
      color: #fa896b;
      font-weight: 600;
      display: block;
      margin-top: 12px;
    }

    /* Si on utilise une classe .attention */
    .message-text ::ng-deep .attention {
      color: #fa896b;
      font-weight: 600;
      display: block;
      margin-top: 12px;
    }

    .dialog-actions {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: 12px;
      padding: 0 24px 24px 24px !important;
      margin: 0 !important;
    }

    .action-btn {
      flex: 1;
      min-width: 200px;
      max-width: 280px;
      padding: 10px 16px;
      font-size: 14px;
      font-weight: 500;
      border-radius: 8px;
      text-transform: none;
      letter-spacing: 0.3px;
      transition: all 0.3s ease;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .option1-btn {
      order: 1;
    }

    .option2-btn {
      order: 2;
    }

    .option3-btn {
      order: 3;
    }

    .bg-primary {
      background-color: #5d87ff !important;
      color: white !important;
    }

    .bg-primary:hover {
      background-color: #4570ea !important;
      box-shadow: 0 4px 12px rgba(93, 135, 255, 0.3);
    }

    .bg-accent {
      background-color: #49beff !important;
      color: white !important;
    }

    .bg-accent:hover {
      background-color: #39aef0 !important;
      box-shadow: 0 4px 12px rgba(73, 190, 255, 0.3);
    }

    .bg-warn {
      background-color: #ff6692 !important;
      color: white !important;
    }

    .bg-warn:hover {
      background-color: #ff4d7d !important;
      box-shadow: 0 4px 12px rgba(255, 102, 146, 0.3);
    }

    .action-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed !important;
      background-color: #e0e0e0 !important;
      color: #9e9e9e !important;
    }

    .action-btn:disabled:hover {
      box-shadow: none !important;
    }

    /* Responsive pour petits écrans */
    @media (max-width: 600px) {
      .dialog-container {
        min-width: 320px;
      }

      .dialog-actions {
        flex-direction: column;
        gap: 10px;
      }

      .action-btn {
        width: 100%;
        max-width: 100%;
      }

      .option1-btn {
        order: 1;
      }

      .option2-btn {
        order: 2;
      }

      .option3-btn {
        order: 3;
      }
    }
  `]
})
export class ChooseThreeDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ChooseThreeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChooseThreeDialogData
  ) { }

  onChoice(choice: 'option1' | 'option2' | 'option3'): void {
    // Empêcher le clic si l'option est désactivée
    if (choice === 'option1' && this.data.option1Disabled) {
      return;
    }
    if (choice === 'option2' && this.data.option2Disabled) {
      return;
    }
    if (choice === 'option3' && this.data.option3Disabled) {
      return;
    }

    const result: ChooseThreeDialogResult = {
      choice: choice,
      value: choice === 'option1' ? this.data.option1Value : 
             choice === 'option2' ? this.data.option2Value : 
             this.data.option3Value
    };
    this.dialogRef.close(result);
  }

  getButtonClass(color: 'primary' | 'accent' | 'warn'): string {
    switch (color) {
      case 'primary':
        return 'bg-primary';
      case 'accent':
        return 'bg-accent';
      case 'warn':
        return 'bg-warn';
      default:
        return 'bg-primary';
    }
  }
}

